import {
  Box,
  Modal,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { MuiMarkdown } from 'mui-markdown'
import dayjs from 'dayjs'
import { JobStatusResponse } from 'src/v3/scribe/scribe.slice'
import { markdownStyles } from 'src/v3/utilities/markdown-styles'

interface SentimentAnalysisModalProps {
  open: boolean
  onClose: () => void
  isLoading: boolean
  analysis: string | null
  error: string | null
  courseName: string | null
  licenseGroup: string | null
  startDate: string | null
  endDate: string | null
  jobStatus: JobStatusResponse | null
  isPolling: boolean
}

let loadingVerbs = [
  'analyzing',
  'pondering',
  'evaluating',
  'synthesizing',
  'interpreting',
  'examining',
  'contemplating',
  'processing',
  'distilling',
  'crafting',
  'mulling',
  'thinking',
  'deciphering',
  'investigating',
  'exploring',
]

export default function SentimentAnalysisModal({
  open,
  onClose,
  isLoading,
  analysis,
  error,
  courseName,
  licenseGroup,
  startDate,
  endDate,
  jobStatus,
  isPolling,
}: SentimentAnalysisModalProps) {
  let [currentVerb, setCurrentVerb] = useState('')

  // Set one random verb when modal opens
  useEffect(() => {
    if (open && isLoading) {
      let getRandomVerb = () =>
        loadingVerbs[Math.floor(Math.random() * loadingVerbs.length)]
      setCurrentVerb(getRandomVerb())
    }
  }, [open, isLoading])

  let getPhaseColor = (
    phase: string,
  ): 'default' | 'primary' | 'secondary' | 'success' | 'error' => {
    switch (phase) {
      case 'JobCreated':
      case 'SessionCreated':
        return 'default'
      case 'BedrockInference':
      case 'ReadingS3':
        return 'primary'
      case 'WritingResponse':
      case 'AnalyzingObjective':
        return 'secondary'
      case 'Complete':
        return 'success'
      case 'Error':
        return 'error'
      default:
        return 'default'
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        sx={{
          width: '90vw',
          height: '90vh',
          overflow: 'auto',
          p: 3,
          position: 'relative',
          bgcolor: 'background.paperDark',
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <Close />
        </IconButton>

        <Typography variant="h5" sx={{ mb: 3, pr: 5 }}>
          Sentiment analysis for {courseName || 'Selected Course'} by{' '}
          {licenseGroup || 'License Group'}
          {startDate && endDate && ` between ${startDate} - ${endDate}`}
        </Typography>

        {/* Loading State */}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              textAlign: 'center',
            }}
          >
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography
              variant="h6"
              sx={{ mb: 1, textTransform: 'capitalize' }}
            >
              {currentVerb}...
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Generating sentiment analysis for your selected criteria
            </Typography>
          </Box>
        )}

        {/* Progress Timeline - Show when polling */}
        {isPolling &&
          jobStatus &&
          jobStatus.progress.length > 0 &&
          !analysis && (
            <Box>
              <LinearProgress sx={{ mb: 3 }} />
              <Typography variant="h6" gutterBottom>
                Progress Timeline
              </Typography>
              <List dense>
                {jobStatus.progress.map((progress, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Chip
                            label={progress.phase}
                            size="small"
                            sx={{
                              color: getPhaseColor(progress.phase),
                            }}
                          />
                          <Typography variant="body2">
                            {dayjs(progress.timestamp).format('HH:mm:ss')}
                          </Typography>
                        </Box>
                      }
                      secondary={progress.message}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

        {/* Error State */}
        {error && !isLoading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography
              color="error"
              variant="h6"
              sx={{
                mb: 2,
              }}
            >
              Analysis Error
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Try adjusting your filters and generate the analysis again.
            </Typography>
          </Box>
        )}

        {/* Results State - Using reusable markdown styles */}
        {analysis && !isLoading && !error && (
          <Box sx={markdownStyles}>
            <MuiMarkdown>{analysis}</MuiMarkdown>
          </Box>
        )}
      </Paper>
    </Modal>
  )
}
